import { makeAutoObservable, runInAction } from "mobx";
import { IAvatar } from "../Common/Avatars/Avatar";
import { supabaseSelect, wrap } from "../util/supabase-wrapper";
import { globalStores } from "./globalStores";
import { supabase, definitions } from "./supabase/client";

type ChildTopicModules = definitions["child_topics_modules_view"];

type ChildLanguageComponents = definitions["child_language_components_view"];

interface ChildLearningProfile {
  topicModules?: ChildTopicModules;
  languageComponents?: ChildLanguageComponents;
}

export class Child {
  childAccount: Partial<definitions["ChildAccount"]> = {};
  userDetail: Partial<definitions["UserDetails"]> = {};
  childDetail: Partial<definitions["ChildDetails"]> = {};
  // talkingPartyStatus: Partial<definitions["TalkingPartyStatus"]> = {};
  classes: Partial<
    Omit<definitions["Class"], "startDate" | "endDate"> & {
      startDate: Date;
      endDate: Date;
    }
  >[] = [];
  curOnboardingStep: number = 0;
  totalOnboardingSteps: number = 4;
  usedFreeTrial: boolean = false;
  childLearningProfile: ChildLearningProfile = {
    topicModules: undefined,
    languageComponents: undefined,
  };

  // classes: Class[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  static async getAllChilds() {
    const childIdObjs = wrap(await supabase.from("ChildAccount").select("id"));

    const allChilds = (
      await Promise.all(
        childIdObjs.map(async (childIdObj) => {
          try {
            const child = new Child();
            return await child.init(childIdObj.id);
          } catch (e) {
            return undefined;
          }
        })
      )
    ).filter(Boolean) as Child[];

    return allChilds;
  }

  static getIcon(child: Child) {
    const avatarIcon = {
      BEAR: "bear",
      DINO: "dino",
      SPIKY_HAIR: "boy",
      HELMET: "biker",
    }[child.childDetail.avatar ?? "BEAR"] as IAvatar["icon"];

    return avatarIcon;
  }

  async init(id: number) {
    this.childAccount.id = id;
    await this.pullChildDetails();
    return this;
  }

  private setTotalOnboardingSteps(newSteps: typeof this.totalOnboardingSteps) {
    this.totalOnboardingSteps = newSteps;
  }

  /**Returns false if the onboarding quiz is already completed */
  async continueOnboardingQuiz(): Promise<boolean> {
    //Set module to onboarding quiz
    //Get id of the last engagement panel completed by student
    //set the last engagement panel id
    const onboardingQuiz = wrap(
      await supabase.from("Module").select("*").match({
        name: "ONBOARDING_QUIZ",
      })
    );

    if (onboardingQuiz.length === 0) {
      throw new Error(
        "Onboarding quiz not set yet! Please ask the teacher to make an onboarding quiz with name 'ONBOARDING_QUIZ'"
      );
    }

    if (onboardingQuiz.length > 1) {
      throw new Error(
        "There should be only one module with the name 'ONBOARDING_QUIZ'! There is currently more than one module named 'ONBOARDING_QUIZ'"
      );
    }

    await globalStores.curModule.init(onboardingQuiz[0].id);

    const moduleEPs = globalStores.curModule.engagementPanels
      .map((ePanel) => ePanel.id!)
      .filter((id) => id !== undefined);

    this.setTotalOnboardingSteps(moduleEPs.length);

    const studentCompletedEPanels = wrap(
      await supabase
        .from("EngagementPanelResult")
        .select("*")
        .in("engagementPanel", moduleEPs)
        .match({ student: this.childAccount.id })
        .is("talkingPartyRoom", null)
    );

    //I think there is completed edge case

    for (let i = moduleEPs.length - 1; i >= 0; i -= 1) {
      const ePanelId = moduleEPs[i];
      const ePanelCompleted = studentCompletedEPanels.some((ePanelResult) => {
        return ePanelResult.engagementPanel === ePanelId;
      });
      //If the latest ePanel is not completed keep checking
      if (!ePanelCompleted) continue;
      //If the last ePanel is completed the quiz is done
      if (i === moduleEPs.length - 1) {
        return false;
      }
      //If the ePanel completed is not the latest one set the current ePanel to the next on to complete
      await globalStores.curEPanel.init(moduleEPs[i + 1]);
      this.curOnboardingStep = i + 1;
      return true;
    }

    //If none of the ePanels have been completed set the current ePanel as the first one
    await globalStores.curEPanel.init(moduleEPs[0]);
    this.curOnboardingStep = 0;

    return true;
  }

  async setAvatar(newAvatar: definitions["ChildDetails"]["avatar"]) {
    await supabase
      .from("ChildDetails")
      .update({ avatar: newAvatar })
      .match({ id: this.childDetail.id })
      .throwOnError();

    await this.pullChildDetails();

    return this;
  }

  checkPasscode(code: number) {
    if (code === this.childDetail.passCode) {
      return true;
    } else {
      return false;
    }
  }

  //TODO: Passcode should be 4 digits long and numerical
  async updatePasscode(parentPassword: string, code: number) {
    if (!globalStores.parentStore.user.email) {
      throw new Error("Missing parent email!");
    }
    const { error } = await supabase.auth.signInWithPassword({
      email: globalStores.parentStore.user.email,
      password: parentPassword,
    });

    if (error) {
      throw new Error("Unable to verify parent password!");
    }

    await supabase
      .from("ChildDetails")
      .update({ passCode: code })
      .match({ id: this.childDetail.id })
      .throwOnError();

    await this.pullChildDetails();

    return this;
  }

  async setPreferredTimeSlot() {}

  //TODO: I think pull Child details is auto refreshing itself or something - need to fix
  async pullChildDetails() {
    if (!this.childAccount.id) {
      throw new Error(
        "ID of child account must be populated with init before running!"
      );
    }

    const allDetails = wrap(
      await supabaseSelect(
        "ChildAccount",
        "*,ChildDetails(*),UserDetails(*),TalkingPartyStatus(*),ChildClass(Class(*))",
        this.childAccount.id
      )
      // await supabase
      //   .from("ChildAccount")
      //   .select(
      //     "*,ChildDetails(*),UserDetails(*),TalkingPartyStatus(*),ChildClass(Class(*))"
      //   )
      //   .match({ id: this.childAccount.id })
    );

    this.childDetail = { ...this.childDetail, ...allDetails[0].ChildDetails };
    this.userDetail = { ...this.userDetail, ...allDetails[0].UserDetails };

    const {
      ChildDetails,
      UserDetails,
      TalkingPartyStatus,
      ...newChildAccount
    } = allDetails[0];
    this.childAccount = newChildAccount;

    this.childAccount.childDetail = this.childDetail.id;
    this.childAccount.userDetail = this.userDetail.id;
    // this.childAccount.talkingPartyStatus = this.talkingPartyStatus.id;

    this.classes = allDetails[0].ChildClass.map((childClass) => {
      return {
        ...childClass.Class,
        startDate: childClass.Class?.startDate
          ? new Date(childClass.Class?.startDate)
          : undefined,
        endDate: childClass.Class?.endDate
          ? new Date(childClass.Class?.endDate)
          : undefined,
      };
    });

    this.usedFreeTrial = await this.hasUsedFreeTrial();
    return this;
  }

  async hasUsedFreeTrial() {
    let results = await supabaseSelect(
      "PaymentStatus",
      `id, product:Product (
          productTier
          ),account`,
      this.childAccount.id!
    );
    // let results = await supabase
    //   .from("PaymentStatus")
    //   .select(
    //     `id, product:Product (
    //       productTier
    //       ),account`
    //   )
    //   .match({ account: this.childAccount.id });

    // Type for product is not captured when joining tables, hence ts-ignore below.
    let filteredResults = results.data?.filter(
      //@ts-ignore TODO: Fix error ts error
      (paymentStatus) => paymentStatus.product?.productTier === "TRIAL"
    );
    return !!(filteredResults?.length && filteredResults.length > 0);
  }

  async joinFreeTrial() {
    let summerPg = wrap(
      await supabase
        .from("Product")
        .select(`id`)
        .match({ productTier: "TRIAL" })
    );
    if (summerPg.length == 0) {
      throw Error("FREE TRIALS NOT IN DB");
    }
    if (await this.hasUsedFreeTrial()) {
      throw Error("The child is already on a free trial!");
    }
    await supabase.from("PaymentStatus").insert({
      account: this.childAccount.id,
      product: summerPg[0]?.id,
    });
  }

  async fetchChildLearningProfileData() {
    await Promise.all([
      this.fetchChildTopicModules(),
      this.fetchChildLanguageComponents(),
    ]);
  }

  async fetchChildTopicModules() {
    if (this.childAccount.id === undefined) {
      throw new Error("Missing child account id");
    }

    const result = wrap(
      await supabase
        .from("child_topics_modules_view")
        .select()
        .eq("ChildAccountID", this.childAccount.id)
    );

    if (result.length === 1) {
      this.childLearningProfile.topicModules = result[0];
    }
  }

  async fetchChildLanguageComponents() {
    if (this.childAccount.id === undefined) {
      throw new Error("Missing child account id");
    }
    const result = wrap(
      await supabase
        .from("child_language_components_view")
        .select()
        .eq("ChildAccountID", this.childAccount.id)
    );

    if (result.length === 1) {
      this.childLearningProfile.languageComponents = result[0];
    }
  }
}

export default new Child();
